import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { StringUtilsHelper } from 'src/app/utils/string-utils-helper';
import { BiStgPlayerBiVizDimsDatasource } from '../../common/sql-maps/bicore-player-dims-sql-map';
import { BIVizDashboardSales } from '../../mopa-sales/sql-maps/biviz-dashboard-sales-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { CampaignTableDashoardItemHelper } from '../items/campaigns-table';
import { BiVizDashboardCampaignsDatasource } from '../sql-maps/biviz-dashboard-campaigns-sql-map';

export interface CampaignsMastertableDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class CampaignsMastertableDataLoaderFactory extends Factory<
    DataLoader,
    CampaignsMastertableDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'CampaignsMastertableDataLoaderFactory';

    getInstance(options: CampaignsMastertableDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                const campaignMastertableDatasource =
                    BiVizDashboardCampaignsDatasource;

                const campaignsMastertableSqlMap =
                    campaignMastertableDatasource.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    campaignsMastertableSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                const activeAll =
                    queryContext.settings[
                        QueryContextSettingID.CAMPAIGN_ACTIVE_ALL
                    ] ?? 'all';
                const activeCampaignSQL: Partial<ICompiledSQLTokens> =
                    activeAll === 'all'
                        ? {}
                        : {
                              where: [`active_campaign IS TRUE`],
                          };

                const campaignsMastertableSql =
                    campaignMastertableDatasource.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options,
                        mergeSqlTokensArray([activeCampaignSQL])
                    );

                const metricDays = CampaignTableDashoardItemHelper.METRIC_DAYS;
                const weeklyDays = CampaignTableDashoardItemHelper.WEEKLY_DAYS;
                const monthlyDays =
                    CampaignTableDashoardItemHelper.MONTHLY_DAYS;
                const allDays = CampaignTableDashoardItemHelper.ALL_DAYS;

                // PIVOT horizon. Days above it never reach the output, so the dense
                // cohort and sales axes can be trimmed before the cumulative windows.
                // Trimming the axis upfront - otherwise the grid spans ~2500 days per cohort.
                const maxDayNo = Math.max(...allDays);

                const salesDatasource = BIVizDashboardSales;

                // identify columns not supported in datasource
                const salesDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    salesDatasource.sqlMap.columns
                );

                const salesDsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    salesDsColumns.missing
                );

                // ignore columns that are not supported in this query
                // const ignoredContextColumnIds = [
                //     'ACTIVITY_DATE',
                //     'ACTIVITY_APP_VERSION',
                //     'ACTIVITY_APP_VERSION_GROUP_MINOR',
                // ];

                //filter out ignored columns from dsColumns.present
                // salesDsColumns.present = salesDsColumns.present.filter(
                //     (col) => !ignoredContextColumnIds.includes(col.id)
                // );

                const salesSQL =
                    salesDatasource.sqlStatementsCompiler.compileSQLStatements(
                        salesDsColumns.present,
                        queryContext.settings,
                        options,
                        { where: [`day_no <= ${maxDayNo}`] }
                    );

                const usersDatasource = BiVizDashboardUsersActivity;

                const usersDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    usersDatasource.sqlMap.columns
                );
                const usersDsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    usersDsColumns.missing
                );
                // Retention is only read on metric days - without this filter
                // COUNT(DISTINCT player_id) runs over the whole activity day axis.
                const usersSQL = usersDatasource.compileSQLStatements(
                    usersDsColumns.present,
                    queryContext.settings,
                    options,
                    {
                        where: [`install_day_no IN (${metricDays.join(', ')})`],
                    }
                );

                const playerBiVizDims = BiStgPlayerBiVizDimsDatasource;
                // identify columns not supported in datasource
                const playerDims = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    playerBiVizDims.sqlMap.columns
                );

                const playerDimsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(playerDims.missing);

                const playerDimsSQL = playerBiVizDims.compileSQLStatements(
                    playerDims.present,
                    queryContext.settings,
                    options,
                    { where: ['player_dim.validation.unified_valid'] }
                );

                const revenueType = (() => {
                    let setting =
                        queryContext.settings[
                            QueryContextSettingID.REVENUE_TYPE
                        ] ?? 'total';
                    if (setting === 'inapp') {
                        return 'inapp_';
                    } else if (setting === 'ad') {
                        return 'ad_';
                    } else if (setting === 'total') {
                        return '';
                    } else {
                        throw new Error(
                            `Unknown revenue type setting: ${setting}`
                        );
                    }
                })();
                // pln/usd
                const currency =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';
                const settingSetGross =
                    queryContext.settings[QueryContextSettingID.NET_GROSS] ??
                    'net';
                const netGross = settingSetGross === 'net' ? 'net_' : 'gross_';

                /**
                 * Generate join condition SQL
                 * @param aliasA
                 * @param aliasB
                 * @returns Example `ON dsA.COLUMN_ID = dsB.COLUMN_ID`
                 */
                const finalDataJoinConditionSql = (options: {
                    aliasA: string;
                    aliasB: string;
                    conditions?: string[];
                }) => {
                    const splitDatasourceColumnIds =
                        queryContext.datasourceColumns
                            .filter((col) => col.state.isSplitApplied)
                            .map(
                                (col) =>
                                    `${options.aliasA}.${col.id} = ${options.aliasB}.${col.id}`
                            );
                    // preadd join condition if there are any conditions
                    if (options.conditions && options.conditions.length > 0) {
                        splitDatasourceColumnIds.unshift(...options.conditions);
                    }
                    // join them with AND
                    return splitDatasourceColumnIds.length > 0
                        ? `ON ${splitDatasourceColumnIds.join(' AND ')}`
                        : '';
                };
                const revenueSql = `${revenueType}revenue_${netGross}${currency}`;

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH running_metrics AS
(
-- Cohorts matching the filters + their full day axis (all_days from cohort_days).
WITH cohorts AS
(
  SELECT
    ${playerDimsSQL.splitSelectAsSeparateColumns(',')}
    ${
        playerBiVizDims.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
    } AS cohort_date,
    COUNT(DISTINCT player_id) AS cohort_size,
    ( SELECT all_days FROM \`${
        options.gcpProjectId
    }.bi_stg.cohort_days\` cd WHERE cd.first_session_date = player_dim.${playerBiVizDims.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL} ) AS all_days
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      playerBiVizDims.sqlMap.dataset,
      playerBiVizDims.sqlMap.tableId
  )}\` player_dim
  ${playerDimsSQL.join}
  ${playerDimsSQL.where}
  GROUP BY ALL
)

-- Dense grid: 1 row = cohort x every day it has matured to, trimmed to the PIVOT horizon.
, cohort_grid AS
(
  SELECT ${playerDimsSQL.splitSelectDatasourceColumnsIds({ suffix: ',' })}
    cohort_date, cohort_size, day_no
  FROM cohorts, UNNEST(all_days) AS day_no
  WHERE day_no <= ${maxDayNo}
)

-- Sparse daily revenue - dashboard_sales only has rows for days with a transaction/ad.
, daily_revenue AS
(
  SELECT
    ${StringUtilsHelper.suffixIfNotNull(
        salesSQL.splitSelectAsSeparateColumns,
        ','
    )}
    ${
        salesDatasource.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
    } AS cohort_date
    , day_no
    , SUM(${revenueSql}) AS revenue
    , SUM(transactions) AS transactions
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      salesDatasource.sqlMap.dataset,
      salesDatasource.sqlMap.tableId
  )}\` ds
  ${salesSQL.join}
  ${salesSQL.where}
  GROUP BY ALL
)

-- Cumulative over the dense axis; cohort maturity changes by day, while gaps
-- in daily sales are carried forward by the dense grid.
, revenue AS
(
  SELECT ${playerDimsSQL.splitSelectDatasourceColumnsIds({ suffix: ',' })}
    day_no
    , SUM(revenue) AS revenue
    , SUM(transactions) AS transactions
    , SUM(running_revenue) AS running_revenue
    , SUM(running_transactions) AS running_transactions
    , SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${playerDimsSQL.splitSelectDatasourceColumnsIds({
          suffix: ',',
          alias: 'g',
      })}
      g.day_no
      , g.cohort_size
      , COALESCE(r.revenue, 0) AS revenue
      , COALESCE(r.transactions, 0) AS transactions
      , SUM(COALESCE(r.revenue, 0)) OVER (
          PARTITION BY ${playerDimsSQL.splitSelectDatasourceColumnsIds({
              suffix: ',',
              alias: 'g',
          })}g.cohort_date
          ORDER BY g.day_no
        ) AS running_revenue
      , SUM(COALESCE(r.transactions, 0)) OVER (
          PARTITION BY ${playerDimsSQL.splitSelectDatasourceColumnsIds({
              suffix: ',',
              alias: 'g',
          })}g.cohort_date
          ORDER BY g.day_no
        ) AS running_transactions
    FROM cohort_grid g
    LEFT JOIN daily_revenue r
      ${finalDataJoinConditionSql({
          aliasA: 'g',
          aliasB: 'r',
          conditions: [`r.cohort_date = g.cohort_date`, `r.day_no = g.day_no`],
      })}
  )
  GROUP BY ALL
)

, present AS
(  
  SELECT
    ${StringUtilsHelper.suffixIfNotNull(
        usersSQL.splitSelectAsSeparateColumns,
        ','
    )}
    install_day_no AS day_no,
    COUNT(DISTINCT player_id) AS present
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      usersDatasource.sqlMap.dataset,
      usersDatasource.sqlMap.tableId
  )}\` ds
  ${usersSQL.join}
  ${usersSQL.where}
  GROUP BY ALL

)

, campaign_cost AS
(  
  SELECT
    ${playerDimsSQL.splitSelectDatasourceColumnsIds({ suffix: ',' })}
    day_no,
    SUM(costs) AS costs
  FROM
  (
    SELECT
    ${StringUtilsHelper.suffixIfNotNull(
        campaignsMastertableSql.splitSelectAsSeparateColumns,
        ','
    )}
            ${
                campaignMastertableDatasource.sqlMap.columns
                    .FIREBASE_FIRST_OPEN_DATE?.columnSQL
            },
      SUM(cost_${currency}) AS costs,
      ( SELECT all_days FROM \`${options.gcpProjectId}.bi_stg.cohort_days\` cd WHERE cd.first_session_date = ds.${campaignMastertableDatasource.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL} ) AS all_days
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        campaignMastertableDatasource.sqlMap.dataset,
        campaignMastertableDatasource.sqlMap.tableId
    )}\` ds
    ${campaignsMastertableSql.where}
    GROUP BY ALL
  ), UNNEST(all_days) AS day_no
  WHERE day_no IN (${allDays.join(', ')})
  GROUP BY ALL
)

, by_fod AS
(
SELECT
    ${playerDimsSQL.splitSelectDatasourceColumnsIds({
        suffix: ',',
        alias: 'b',
    })}
    b.day_no,
    revenue,
    running_revenue,
    cohort_size,
    costs,

    -- Current logic:
    -- Total revenue up to the given day / Only mature cohorts OR costs that reached that life day
    ROUND(SAFE_DIVIDE(b.running_revenue, cohort_size), 4) AS arpu,
    ROUND(SAFE_DIVIDE(b.running_revenue, costs), 4) AS roas,

    ROUND(SAFE_DIVIDE(campaigns.costs, b.running_transactions), 4) AS cpt,
    ROUND(SAFE_DIVIDE(p.present, cohort_size), 4) AS retention
  FROM revenue b
  LEFT JOIN campaign_cost campaigns
    ${finalDataJoinConditionSql({
        aliasA: 'b',
        aliasB: 'campaigns',
        conditions: [`b.day_no = campaigns.day_no`],
    })}
  LEFT JOIN present p
    ${finalDataJoinConditionSql({
        aliasA: 'b',
        aliasB: 'p',
        conditions: [`b.day_no = p.day_no`],
    })}
  WHERE b.day_no IN (${allDays.join(', ')})
)
  
  -- Pivot to single row with selected day ARPUs
  SELECT *
  FROM by_fod
  PIVOT( MAX(revenue) AS revenue
    , MAX(running_revenue) AS running_revenue
    , MAX(retention) AS retention
    , MAX(cohort_size) AS cohort_size
    , MAX(costs) AS costs
    , MAX(arpu) AS arpu
    , MAX(roas) AS roas
    , MAX(cpt) AS cpt 
  FOR day_no IN (${allDays.map((d) => `${d} as d${d}`).join(', ')}) )
)

, mastertable_base AS
(
  SELECT
    ${StringUtilsHelper.suffixIfNotNull(
        campaignsMastertableSql.splitSelectAsSeparateColumns,
        ','
    )}
    -- source: AppsFlyer
    ROUND(SUM(cost_${currency}), 2) AS costs
    -- source: BIEvents
    , ROUND(SUM(${revenueType}revenue_${netGross}${currency}), 2) AS income
    -- source: BIEvents + reattribution from BIEvents level campaign info
    , ROUND(SUM(reattr_${revenueType}revenue_${netGross}${currency}), 2) AS reattribution_income
    -- source: revenue from BIEvents, cost from AppsFlyer
    , ROUND(SAFE_DIVIDE(SUM(${revenueType}revenue_${netGross}${currency}), SUM(cost_${currency})), 4) AS total_roas
    -- source: revenue from BIEvents, cost from AppsFlyer
    , ROUND((COALESCE(SUM(${revenueType}revenue_${netGross}${currency}), 0) - COALESCE(SUM(cost_${currency}), 0)), 2) AS total_profit
    -- source: BIEvents
    , ROUND(SAFE_DIVIDE(SUM(ad_revenue_${netGross}${currency}), SUM(revenue_${netGross}${currency})), 4) AS ad_revenue_share
    -- source: AppsFlyer
    , SUM(impressions) AS impressions
    -- source: AppsFlyer
    , SUM(installs) AS installs
    -- source: BIEvents
    , SUM(transaction_count) AS transactions
    -- source: BIEvents
    , SUM(payers) AS payers
    -- source: BIEvents
    , SUM(payers_d7) AS payers_d7
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(installs)), 2) AS cpi
    -- cost: - AppsFlyer; transactions: BIEvents
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(transaction_count)), 2) AS cpt
    , ROUND(1000 * SAFE_DIVIDE(SUM(cost_${currency}), SUM(impressions)), 2) AS cpm
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(payers)), 2) AS cppu
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(payers_d7)), 2) AS cppu_d7
    , ROUND(1000 * SAFE_DIVIDE(SUM(installs), SUM(impressions)), 2) AS ipm
    --  source: AppsFlyer
    , ROUND(SUM(reattributions), 2) AS reattributions
  
    
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      campaignsMastertableSqlMap.dataset,
      campaignsMastertableSqlMap.tableId
  )}\`
  ${campaignsMastertableSql.where}
  ${
      campaignsMastertableSql.splitGroupByAsSeparateColumns
          ? `GROUP BY ${campaignsMastertableSql.splitGroupByAsSeparateColumns}`
          : ''
  }
)

SELECT
  m.*
  ${metricDays.map((d) => `, runnings.revenue_d${d}`).join('\n')}
  ${metricDays.map((d) => `, runnings.running_revenue_d${d}`).join('\n')}
  ${metricDays.map((d) => `, runnings.cohort_size_d${d}`).join('\n')}
  ${metricDays.map((d) => `, runnings.costs_d${d}`).join('\n')}
  ${metricDays.map((d) => `, runnings.arpu_d${d}`).join('\n')}  
  ${metricDays.map((d) => `, runnings.roas_d${d}`).join('\n')}
  ${weeklyDays
      .map(
          (w) =>
              `, runnings.roas_d${w.day_no} AS roas_w${w.week_no}_d${w.day_no}`
      )
      .join('\n')}
  ${monthlyDays
      .map(
          (m) =>
              `, runnings.roas_d${m.day_no} AS roas_m${m.month_no}_d${m.day_no}`
      )
      .join('\n')}
  ${metricDays.map((d) => `, runnings.cpt_d${d}`).join('\n')}
  ${metricDays.map((d) => `, runnings.retention_d${d}`).join('\n')}
FROM mastertable_base m
LEFT JOIN running_metrics runnings
  ${finalDataJoinConditionSql({ aliasA: 'm', aliasB: 'runnings' })}

${
    campaignsMastertableSql.splitGroupByAsSeparateColumns
        ? `ORDER BY ${campaignsMastertableSql.splitGroupByAsSeparateColumns}`
        : ''
}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
